import type {
  AskUserQuestionMultipleChoice,
  AskUserQuestionOneOfTwo,
  AskUserQuestionPhotoUpload,
  AskUserQuestionScale,
  AskUserQuestionSingleChoice,
  AskUserQuestionYesOrNo,
} from "@/api/generated/models";
import MultipleChoiceQuestion from "@/conversation/AssistantMessage/AskUserQuestions/MultipleChoiceQuestion";
import OneOfTwoQuestion from "@/conversation/AssistantMessage/AskUserQuestions/OneOfTwoQuestion";
import PhotoUploadQuestion from "@/conversation/AssistantMessage/AskUserQuestions/PhotoUploadQuestion";
import ScaleQuestion from "@/conversation/AssistantMessage/AskUserQuestions/ScaleQuestion";
import SingleChoiceQuestion from "@/conversation/AssistantMessage/AskUserQuestions/SingleChoiceQuestion";
import YesOrNoQuestion from "@/conversation/AssistantMessage/AskUserQuestions/YesOrNoQuestion";
import type { AskUserAnswer } from "@/store/askUserQuestions";
import { QuestionScrollArea } from "./OptionParts";

export type AskUserQuestion =
  | AskUserQuestionMultipleChoice
  | AskUserQuestionSingleChoice
  | AskUserQuestionOneOfTwo
  | AskUserQuestionYesOrNo
  | AskUserQuestionScale
  | AskUserQuestionPhotoUpload;

const EMPTY_CHOICE = { selected: [], otherText: "" };

export default function QuestionBody({
  question,
  questionIndex,
  answer,
  onChange,
  onSubmit,
  onSkip,
  isLast,
}: {
  question: AskUserQuestion;
  questionIndex: number;
  answer: AskUserAnswer | undefined;
  onChange: (answer: AskUserAnswer) => void;
  onSubmit: () => void;
  onSkip: () => void;
  isLast: boolean;
}) {
  const choiceValue = answer?.type === "choice" ? answer : EMPTY_CHOICE;
  const onChoiceChange = (value: { selected: string[]; otherText: string }) =>
    onChange({ type: "choice", ...value });
  const controls = { onSubmit, onSkip, isLast };

  switch (question.question_type) {
    case "yes_or_no":
      return (
        <QuestionScrollArea>
          <YesOrNoQuestion
            question={question}
            value={choiceValue}
            onChange={onChoiceChange}
            {...controls}
          />
        </QuestionScrollArea>
      );
    case "one_of_two":
      return (
        <QuestionScrollArea>
          <OneOfTwoQuestion
            question={question}
            value={choiceValue}
            onChange={onChoiceChange}
            {...controls}
          />
        </QuestionScrollArea>
      );
    case "single_choice":
      return (
        <SingleChoiceQuestion
          options={question.options}
          value={choiceValue}
          onChange={onChoiceChange}
          {...controls}
        />
      );
    case "multiple_choice":
      return (
        <MultipleChoiceQuestion
          options={question.options}
          value={choiceValue}
          onChange={onChoiceChange}
          {...controls}
        />
      );
    case "scale":
      return (
        <QuestionScrollArea>
          <ScaleQuestion
            question={question}
            value={answer?.type === "scale" ? answer.value : ""}
            onChange={(value) => onChange({ type: "scale", value })}
            {...controls}
          />
        </QuestionScrollArea>
      );
    case "photo_upload":
      return (
        <QuestionScrollArea>
          <PhotoUploadQuestion
            question={question}
            questionIndex={questionIndex}
            value={answer?.type === "photo" ? answer.attachmentIds : []}
            onChange={(attachmentIds) =>
              onChange({ type: "photo", attachmentIds })
            }
            {...controls}
          />
        </QuestionScrollArea>
      );
    default:
      return null;
  }
}
